import React, { useState } from 'react'
import { TextInput } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { colors } from '../../tokens'
import { Box } from '../box'
import { CalendarGlyph, ClockGlyph, GLYPH_COLORS } from '../picker-icons/pickerGlyphs'
import { Pressable } from '../pressable'
import { CloseGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'
import { Tooltip, TooltipContent, TooltipText } from '../tooltip'
import { dateTimeFieldClasses, type DateTimeInputState, type DateTimeInputType } from './variants'

export type DateTimeInputProps = {
    type?: DateTimeInputType
    label?: string
    required?: boolean
    value?: string
    placeholder?: string
    state?: DateTimeInputState
    editable?: boolean
    onChangeText?: (text: string) => void
    onSubmit?: () => void
    onIconPress?: () => void
    onClear?: () => void
    clearLabel?: string
    onPress?: () => void
    tooltip?: string
    className?: string
    testID?: string
}

const labelText = 'text-[0.875rem] leading-[1.25rem] text-content-default'
const valueText = 'text-[0.875rem] leading-[1.25rem]'
const REQUIRED_MARK = '*'

export const DateTimeInput: React.FC<DateTimeInputProps> = ({
    type = 'date',
    label,
    required,
    value,
    placeholder,
    state = 'default',
    editable = false,
    onChangeText,
    onSubmit,
    onIconPress,
    onClear,
    clearLabel = 'Clear',
    onPress,
    tooltip,
    className,
    testID,
}) => {
    const [focused, setFocused] = useState(false)
    const isDisabled = state === 'disabled'
    const hasValue = Boolean(value)
    const TrailingIcon = type === 'time' ? ClockGlyph : CalendarGlyph
    const effectiveState: DateTimeInputState = state === 'default' && focused ? 'focus' : state

    const valueField = editable ? (
        <TextInput
            className={twMerge(valueText, 'min-w-0 flex-1 text-content-default')}
            value={value ?? ''}
            placeholder={placeholder}
            placeholderTextColor={colors.content.muted}
            editable={!isDisabled}
            onChangeText={onChangeText}
            onFocus={() => setFocused(true)}
            onBlur={() => {
                setFocused(false)
                onSubmit?.()
            }}
            onSubmitEditing={onSubmit}
            testID={testID}
        />
    ) : (
        <Box className='flex-1 flex-row items-center gap-2'>
            <Text
                className={twMerge(
                    valueText,
                    hasValue ? 'text-content-default' : 'text-content-muted',
                )}
            >
                {hasValue ? value : placeholder}
            </Text>
        </Box>
    )

    const iconButton = (triggerProps?: object) => (
        <Pressable
            {...triggerProps}
            onPress={isDisabled ? undefined : (onIconPress ?? onPress)}
            accessibilityRole='button'
            accessibilityLabel={tooltip ?? `Select ${type}`}
        >
            <TrailingIcon size={24} color={GLYPH_COLORS.default} />
        </Pressable>
    )

    const row = (
        <Box className={twMerge(dateTimeFieldClasses(effectiveState), 'border-solid')}>
            {valueField}
            {onClear && hasValue && !isDisabled && (
                <Pressable
                    accessibilityRole='button'
                    accessibilityLabel={clearLabel}
                    onPress={onClear}
                    className='mx-2 h-6 w-6 shrink-0 items-center justify-center'
                >
                    <CloseGlyph size={20} />
                </Pressable>
            )}
            {tooltip ? (
                <Tooltip placement='top' trigger={triggerProps => iconButton(triggerProps)}>
                    <TooltipContent>
                        <TooltipText>{tooltip}</TooltipText>
                    </TooltipContent>
                </Tooltip>
            ) : (
                iconButton()
            )}
        </Box>
    )

    return (
        <Box className={twMerge('flex-col items-start gap-1 w-[220px]', className)}>
            {label ? (
                <Box className='flex-row items-center gap-0.5 px-1'>
                    <Text className={labelText}>{label}</Text>
                    {required ? <Text className={labelText}>{REQUIRED_MARK}</Text> : null}
                </Box>
            ) : null}
            {editable ? (
                row
            ) : (
                <Pressable
                    className='w-full'
                    onPress={isDisabled ? undefined : onPress}
                    accessibilityRole='button'
                    accessibilityLabel={label ?? `Select ${type}`}
                    testID={testID}
                >
                    {row}
                </Pressable>
            )}
        </Box>
    )
}

DateTimeInput.displayName = 'DateTimeInput'
